import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyGetBranchListQuery } from "store/services/branchApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import ExportBranchListExcel from "./branchComponents/ExportBranchListExcel";

const BranchListScreen = () => {
  const { isAdmin, isVendor, isEntity } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const [
    fetchBranches,
    {
      data: branchRes,
      isLoading: loadingBranches,
      isFetching: fetchingBranches,
      error,
    },
  ] = useLazyGetBranchListQuery();

  React.useEffect(() => {
    fetchBranches({
      status: activeTab === "MyBranches" ? "Active" : activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { search: keyword }),
      ...(activeTab === "MyBranches" && { myBranches: true }),
    });
  }, [activeTab, currentPage, fetchBranches, keyword, perPage]);

  useAppMeta(`${activeTab} Branches`);

  const columns = [
    {
      name: "Code",
      selector: (row) => row.code,
      cell: (row) => (
        <>
          {row?.code ? (
            <HighlightedText text={row?.code} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.name}
          to={`/app/crm/branch/${row._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
      minWidth: "200px",
      sortable: true,
    },
    {
      name: "Address",
      selector: (row) => row.address,
      cell: (row) => (
        <>
          {row?.address ? (
            <HighlightedText text={row?.address} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
      sortable: true,
    },
    {
      name: "Region",
      selector: (row) => row.region?.name,
      cell: (row) => (
        <>
          {row?.region?.name ? (
            <HighlightedText text={row?.region?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
      sortable: true,
    },
    {
      name: "Warehouse",
      selector: (row) => row.warehouse?.name,
      cell: (row) => (
        <>
          {row?.warehouse?.name ? (
            <HighlightedText text={row?.warehouse?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      cell: (row) => {
        let phone = row?.phone || "";
        const lastDash = phone.lastIndexOf("-");
        if (lastDash !== -1) {
          phone = phone.substring(0, lastDash).trim();
        }
        return (
          <>
            {phone ? (
              <HighlightedText text={phone} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </>
        );
      },
      sortable: true,
    },
    {
      name: "Area Covered",
      selector: (row) => row.areasCovered,
      cell: (row) => (
        <>
          {row?.areasCovered ? (
            <HighlightedText text={row?.areasCovered} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              isAdmin
                ? `/app/crm/branch/${row._id}/detail`
                : `/app/vendor/branch/${row._id}/detail`
            }
          >
            <IconButton size="small" aria-label="View Branch" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          {isAdmin && (
            <Link
              to={`/app/crm/branch/${row._id}/edit`}
              state={{ from: "list" }}
            >
              <IconButton size="small" aria-label="Edit Branch">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )}
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const tabs = [
    { label: "Active", value: "Active" },
    !isVendor && { label: "Inactive", value: "Inactive" },
    isEntity && !isVendor && { label: "My Branches", value: "MyBranches" },
  ].filter(Boolean);

  return (
    <BreadcrumbLayout
      breadcrumbs={[{ title: "Branches" }]}
      headerActions={activeTab === "Active" && <PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={tabs} activeTab={activeTab} />
        <TableFilter
          searchLabel="Name"
          disabled={loadingBranches || fetchingBranches}
          actions={
            <ExportBranchListExcel
              disabled={loadingBranches || fetchingBranches}
              filters={{
                status: activeTab === "MyBranches" ? "Active" : activeTab,
                ...(keyword && { search: keyword }),
                ...(activeTab === "MyBranches" && { myBranches: true }),
              }}
            />
          }
        >
          <div className="font-medium">{activeTab} Branches</div>
        </TableFilter>
        <AppTable
          columns={columns}
          data={branchRes?.data ?? []}
          progressPending={loadingBranches || fetchingBranches}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={branchRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => {
  const { isAdmin } = useAuth();

  if (!isAdmin) return null;

  return (
    <Link to="/app/crm/branch/create">
      <Button startIcon={<Add />} variant="contained">
        New Branch
      </Button>
    </Link>
  );
};

export default BranchListScreen;
